import { useSaveFailureToast } from '@/composables/useSaveFailureToast'
import { useSaveQueue } from '@/composables/useSaveQueue'
import { userLeftTheField } from '@/utils/field-leave'
import { router } from '@inertiajs/vue3'
import { ref, type Ref } from 'vue'

export interface KeyedRow {
  key: string
  id?: string | null
  placeholder?: boolean
}

type StoredRow<TRow extends KeyedRow> = TRow & { id: string }

interface PersistedRowsOptions<TRow extends KeyedRow, TPayload> {
  toPayload: (row: TRow) => TPayload
  isDraft: (row: TRow) => boolean
  create: (payload: TPayload, key: string) => Promise<string>
  update: (id: string, payload: TPayload, key: string) => Promise<void>
  destroy: (id: string) => Promise<void>
  reloadProps: string[]
  canSave?: () => boolean
  saveOrder?: (row: TRow, saved: TPayload | undefined) => number
}

export interface PersistedRows<TRow extends KeyedRow> {
  rows: Ref<TRow[]>
  reset: (serverRows: TRow[]) => void
  addDraft: (row: TRow) => void
  removeRow: (key: string) => void
  ensureSaved: (key: string) => Promise<string>
  persistChanges: () => void
  persistOnLeave: () => void
}

const TABLE = 'table'

export const matchesBlankRow =
  <TRow, TPayload>(toPayload: (row: TRow) => TPayload, blankRow: () => TRow) =>
  (row: TRow): boolean =>
    JSON.stringify(toPayload(row)) === JSON.stringify(toPayload(blankRow()))

export function idOf(created: { id?: string | null }): string {
  if (!created.id) {
    throw new Error('The backend answered a created row without its id.')
  }

  return created.id
}

export function usePersistedRows<TRow extends KeyedRow, TPayload>({
  toPayload,
  isDraft,
  create,
  update,
  destroy,
  reloadProps,
  canSave = () => true,
  saveOrder,
}: PersistedRowsOptions<TRow, TPayload>): PersistedRows<TRow> {
  const rows = ref([]) as Ref<TRow[]>
  const savedPayloads = new Map<string, string>()
  const removals = new Set<string>()
  const forced = new Set<string>()
  const creating = new Set<string>()
  const queue = useSaveQueue()
  const reportSaveFailure = useSaveFailureToast()

  const serialize = (row: TRow) => JSON.stringify(toPayload(row))

  const rowOf = (key: string) => rows.value.find((row) => row.key === key)

  const isStored = (row: TRow): row is StoredRow<TRow> => typeof row.id === 'string' && row.id !== ''

  const isUnsaved = (row: TRow) => savedPayloads.get(row.key) !== serialize(row)

  const needsSave = (row: TRow): boolean => {
    if (removals.has(row.key)) {
      return false
    }

    return isStored(row) ? isUnsaved(row) : forced.has(row.key) || !isDraft(row)
  }

  const savedPayloadOf = (row: TRow): TPayload | undefined => {
    const saved = savedPayloads.get(row.key)

    return saved === undefined ? undefined : (JSON.parse(saved) as TPayload)
  }

  const withoutRow = (key: string) => rows.value.filter((row) => row.key !== key)

  const reloadFromServer = () => router.reload({ only: [...reloadProps, 'navBadges'] })

  /**
   * Fresh server rows arrive whenever the page reloads its props — also while an edit is still
   * waiting to be sent. Rows are matched by id: a row with such an edit keeps its local value, and
   * rows that exist only in the browser stay at the end.
   */
  function reset(serverRows: TRow[]): void {
    const localRows = new Map(rows.value.filter(isStored).map((row) => [row.id, row]))
    const localDrafts = new Map(
      rows.value.filter((row) => !isStored(row) && !row.placeholder).map((row) => [row.key, row]),
    )
    const previousSaved = new Map(savedPayloads)

    savedPayloads.clear()

    const merged = serverRows.map((server) => {
      if (!server.id) {
        const draft = localDrafts.get(server.key)

        localDrafts.delete(server.key)

        return draft ?? server
      }

      const local = localRows.get(server.id)
      const saved = local ? previousSaved.get(local.key) : undefined

      if (local && saved !== undefined && saved !== serialize(local)) {
        savedPayloads.set(local.key, saved)

        return local
      }

      const row = local ? { ...server, key: local.key } : server

      savedPayloads.set(row.key, serialize(row))

      return row
    })

    rows.value = [...merged, ...localDrafts.values()]
  }

  function addDraft(row: TRow): void {
    rows.value = [...rows.value, row]
  }

  async function removeMarkedRows(): Promise<boolean> {
    let removed = false

    for (const key of [...removals]) {
      const row = rowOf(key)

      removals.delete(key)

      if (row && isStored(row)) {
        try {
          await destroy(row.id)
        } catch (error) {
          reportSaveFailure(error)

          continue
        }

        removed = true
      }

      rows.value = withoutRow(key)
      savedPayloads.delete(key)
    }

    return removed
  }

  async function saveRow(key: string): Promise<boolean> {
    const row = rowOf(key)

    if (!row || !needsSave(row)) {
      return false
    }

    const payload = toPayload(row)

    try {
      if (isStored(row)) {
        await update(row.id, payload, key)
      } else {
        creating.add(key)

        const id = await create(payload, key)

        rows.value = rows.value.map((candidate) => (candidate.key === key ? { ...candidate, id } : candidate))
      }
    } catch (error) {
      reportSaveFailure(error)

      return false
    } finally {
      creating.delete(key)
    }

    savedPayloads.set(key, JSON.stringify(payload))
    forced.delete(key)

    return true
  }

  const keysToSave = (): string[] => {
    const pending = rows.value.filter(needsSave)
    const ordered = saveOrder
      ? [...pending].sort((a, b) => saveOrder(a, savedPayloadOf(a)) - saveOrder(b, savedPayloadOf(b)))
      : pending

    return ordered.map((row) => row.key)
  }

  async function syncWithServer(): Promise<void> {
    let changed = await removeMarkedRows()

    if (canSave()) {
      for (const key of keysToSave()) {
        changed = (await saveRow(key)) || changed
      }
    }

    if (changed) {
      reloadFromServer()
    }
  }

  function persistChanges(): void {
    queue.schedule(TABLE, syncWithServer)
  }

  function persistOnLeave(): void {
    if (userLeftTheField()) {
      persistChanges()
    }
  }

  function removeRow(key: string): void {
    const row = rowOf(key)

    if (!row) {
      return
    }

    if (!isStored(row) && !creating.has(key)) {
      rows.value = withoutRow(key)
      forced.delete(key)

      return
    }

    removals.add(key)
    persistChanges()
  }

  async function ensureSaved(key: string): Promise<string> {
    forced.add(key)
    persistChanges()

    await queue.flush()

    const id = rowOf(key)?.id

    if (!id) {
      throw new Error(`The row ${key} could not be saved.`)
    }

    return id
  }

  return { rows, reset, addDraft, removeRow, ensureSaved, persistChanges, persistOnLeave }
}
